import { token } from '@atlaskit/tokens';
import { VISUAL_REFRESH_TABLE_BORDER_RADIUS } from '@databases/styles';
import React, { useLayoutEffect, useRef, useState, type ReactNode } from 'react';
import styled from 'styled-components';

import { useEmbedSelected } from './state';

const LAYOUT_TABLE_ID = 'layout-table';
const ADD_ENTRY_QUICK_CALC_ID = 'add-entry-quick-calc-container';
const SCROLL_INNER_TEST_ID = 'scrollable-inner-container';
const CONTENT_RESIZE_OBSERVER_ID = 'resizeObserver';

const BaseWrapper = styled.div`
  display: flex;
  flex-direction: column;
  flex-grow: 1;
  min-height: 0;
  height: 100%;
  position: relative;
`;

const SelectionOverlay = styled.div<{
  $top: number;
  $height: number;
}>`
  position: absolute;
  top: ${(p) => p.$top}px;
  left: 0;
  right: 0;
  height: ${(p) => p.$height}px;
  box-sizing: border-box;
  pointer-events: none;
  border: 1px solid ${token('color.border.selected', '#0C66E4')};
  border-radius: ${VISUAL_REFRESH_TABLE_BORDER_RADIUS};
  /**
   * Sits below SIDE_PANEL_CONTAINER (200) so the side panel renders on top,
   * but above most content layers.
   */
  z-index: 199;
`;

type BorderBox = { top: number; height: number };

/**
 * Returns the top position of the visible table header by querying the first
 * `<th>` cell. Falls back to `<thead>`, then the table element itself.
 *
 * The `<thead>` uses `display: contents` and the `<tr>` is `position: sticky`,
 * so their bounding rects may not align with the visual top of the header cells.
 */
function getTableTopForBorder(table: HTMLElement): number {
  const firstTh = table.querySelector<HTMLElement>('thead th');
  if (firstTh) {
    return firstTh.getBoundingClientRect().top;
  }
  const thead = table.querySelector<HTMLElement>('thead');
  if (thead) {
    return thead.getBoundingClientRect().top;
  }
  return table.getBoundingClientRect().top;
}

/**
 * Measures the border box for the selection overlay relative to the container.
 *
 * For table views: aligns the top edge to the first header cell and the bottom
 * edge to the add-entry / quick-calc row (or the table bottom if absent).
 *
 * For gallery / board views: wraps the full container.
 */
function measureBorderBox(container: HTMLElement): BorderBox | null {
  const containerRect = container.getBoundingClientRect();

  const table = document.getElementById(LAYOUT_TABLE_ID);
  if (!table) {
    return {
      top: 0,
      height: Math.round(containerRect.height)
    };
  }

  const tableTop = getTableTopForBorder(table);
  const addRow = document.getElementById(ADD_ENTRY_QUICK_CALC_ID);
  const bottomRect = (addRow ?? table).getBoundingClientRect();

  const clampedTop = Math.max(containerRect.top, tableTop);
  const clampedBottom = Math.min(containerRect.bottom, bottomRect.bottom);

  const top = clampedTop - containerRect.top;
  const height = clampedBottom - clampedTop;

  return {
    top: Math.round(Math.max(0, top)),
    height: Math.round(Math.max(0, height))
  };
}

/**
 * Wraps the database content and, when the embed is selected, renders a 1px
 * selection border overlay. For table views the border aligns to the header
 * and footer rows; for other layouts it wraps the full container.
 */
export function NativeEmbedSelectionBorder({
  children
}: {
  children: ReactNode;
}): JSX.Element {
  const isSelected = useEmbedSelected();
  const containerRef = useRef<HTMLDivElement>(null);
  const [box, setBox] = useState<BorderBox | null>(null);

  useLayoutEffect(() => {
    if (!isSelected) {
      setBox(null);
      return;
    }

    const container = containerRef.current;
    if (!container) {
      return;
    }

    let rafId: number | null = null;
    let lastScrollTime = 0;
    const SCROLL_THROTTLE_MS = 16; // ~60fps

    const remeasure = () => {
      setBox(measureBorderBox(container));
    };

    const scheduleRemeasure = () => {
      if (rafId !== null) {
        cancelAnimationFrame(rafId);
      }
      rafId = requestAnimationFrame(() => {
        rafId = null;
        remeasure();
      });
    };

    const throttledScrollRemeasure = () => {
      const now = performance.now();
      if (now - lastScrollTime < SCROLL_THROTTLE_MS) {
        return;
      }
      lastScrollTime = now;
      scheduleRemeasure();
    };

    remeasure();

    const scrollTarget = container.querySelector<HTMLElement>(
      `[data-testid="${SCROLL_INNER_TEST_ID}"]`
    );

    scrollTarget?.addEventListener('scroll', throttledScrollRemeasure, { passive: true });
    window.addEventListener('resize', scheduleRemeasure);

    const resizeObserver = new ResizeObserver(scheduleRemeasure);
    resizeObserver.observe(container);
    const contentResizeTarget = document.getElementById(CONTENT_RESIZE_OBSERVER_ID);
    if (contentResizeTarget && contentResizeTarget !== container) {
      resizeObserver.observe(contentResizeTarget);
    }

    return () => {
      if (rafId !== null) {
        cancelAnimationFrame(rafId);
      }
      scrollTarget?.removeEventListener('scroll', throttledScrollRemeasure);
      window.removeEventListener('resize', scheduleRemeasure);
      resizeObserver.disconnect();
    };
  }, [isSelected]);

  return (
    <BaseWrapper ref={containerRef}>
      {children}
      {isSelected && box && box.height > 0 && (
        <SelectionOverlay
          $top={box.top}
          $height={box.height}
          data-testid="native-embed-selection-border"
        />
      )}
    </BaseWrapper>
  );
}
